import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { resolveTeams, suggestTeams, type EmployeeImportRow } from "@graftus/schema";
import type { SectorWords } from "@/hooks/use-sector-words";
import { StepHeading } from "../OnboardingShell";
import { StepFooter } from "../StepFooter";
import { SummaryBanner } from "../ui/SummaryBanner";
import { TeamCard } from "../ui/TeamCard";
import type { ImportSummary } from "./EmployeesStep";

interface TeamsStepProps {
  words: SectorWords;
  rows: EmployeeImportRow[];
  /** Raw team value -> name chosen by the admin. */
  teamNames: Record<string, string>;
  onRename: (rawKey: string, name: string) => void;
  importSummary: ImportSummary | null;
  onNext: () => void;
  onBack?: () => void;
  importing?: boolean;
  error?: string | null;
}

/** Step 5: teams suggested from the team column; rename inline, same name merges. */
export function TeamsStep({
  words,
  rows,
  teamNames,
  onRename,
  importSummary,
  onNext,
  onBack,
  importing = false,
  error,
}: TeamsStepProps) {
  const { t } = useTranslation("onboarding");
  const suggestions = useMemo(() => suggestTeams(rows), [rows]);
  const resolved = useMemo(() => resolveTeams(rows, teamNames), [rows, teamNames]);
  const nameById = useMemo(() => new Map(rows.map((row) => [row.id, row.name])), [rows]);

  // Which raw keys share an effective name with another card.
  const mergedInto = new Map<string, string>();
  for (const team of resolved) {
    if (team.sources.length > 1) {
      for (const source of team.sources) mergedInto.set(source, team.name);
    }
  }

  const importedTotal = importSummary ? importSummary.created + importSummary.updated : 0;
  const nothingToDo = rows.length === 0;

  return (
    <div data-testid="onboarding-teams">
      <StepHeading
        title={t("teams.title")}
        lead={t("teams.lead", { teamPluralDef: words.teamPluralDef })}
      />

      {nothingToDo ? (
        <SummaryBanner
          tone={importedTotal > 0 ? "success" : "info"}
          title={
            importedTotal > 0
              ? t("teams.imported", { n: importedTotal, teams: importSummary?.teamsCreated ?? 0 })
              : t("teams.empty", { team: words.team })
          }
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {suggestions.map((suggestion) => {
            const name = teamNames[suggestion.key] ?? suggestion.key;
            const mergeTarget = mergedInto.get(suggestion.key);
            return (
              <TeamCard
                key={suggestion.key}
                id={suggestion.key}
                name={name}
                nameLabel={t("teams.nameLabel")}
                onRename={(next) => onRename(suggestion.key, next)}
                memberNames={suggestion.memberIds.map((id) => nameById.get(id) ?? "")}
                countLabel={t("teams.count", { n: suggestion.memberIds.length })}
                originLabel={
                  name.trim().toLowerCase() !== suggestion.key.toLowerCase()
                    ? t("teams.from", { team: words.team, raw: suggestion.key })
                    : null
                }
                mergeHint={mergeTarget ? t("teams.mergeHint", { name: mergeTarget }) : null}
              />
            );
          })}
        </div>
      )}

      <p className="mt-5 rounded-g-md bg-g-primary-wash px-4 py-3 text-[13.5px] leading-relaxed text-g-ink-700">
        {t("teams.note")}
      </p>

      {error ? (
        <p className="mt-4 rounded-g-md border border-g-danger bg-g-danger-tint px-4 py-3 text-[14px] font-medium text-g-danger" role="alert">
          {error}
        </p>
      ) : null}

      <StepFooter
        primaryLabel={importing ? t("teams.importing") : t("teams.next")}
        onPrimary={onNext}
        primaryDisabled={nothingToDo && importedTotal === 0}
        loading={importing}
        onBack={onBack}
      />
    </div>
  );
}
